import { Formik, Form, Field, ErrorMessage } from "formik";
import React, { useState } from "react";
import '../users/userform.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import InternalFooter from '../components/InternalFooter/InternalFooter';
import axiosInstance from "../../services/AxiosInstance";
import { saveTokenInLocalStorage, saveUserDataLocalStorage } from "../../services/AuthService";
import { useDispatch } from "react-redux";
import { loginConfirmedAction } from "../../store/actions/AuthActions";

// Validation Schema with Yup
const ChangePasswordSchema = Yup.object().shape({
  currentPassword: Yup.string().required("Current password is required"),
  newPassword: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("New password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const ChangePassword = ({ userId, onPasswordChanged, navigate }) => {
  const dispatch = useDispatch();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
     try {
      const payload={current_password:values.currentPassword,new_password:values.newPassword};
      const response = await axiosInstance.post("/settings/changePassword",payload);
      if (response && response.data) {
        const { accessToken, user } = response.data;
        const apiTimestamp = response.data.timestamp || response.data.issuedAt || response.data.createdAt;
        if (accessToken && user) {
          const rememberMe = localStorage.getItem('rememberMe') === 'true';
          saveTokenInLocalStorage(accessToken, rememberMe, apiTimestamp);
          saveUserDataLocalStorage(user);
          dispatch(loginConfirmedAction({ token: accessToken, user }));
        }
        toast.success("Password changed successfully!");
        resetForm();
        if (onPasswordChanged) onPasswordChanged();
      }
    } catch (err) {
      toast.error(err.response.data.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="row" style={{ marginBottom: "0px !important" }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-body">
              <section>
                <Formik
                  initialValues={{
                    currentPassword: "",
                    newPassword: "",
                    confirmPassword: "",
                  }}
                  validationSchema={ChangePasswordSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <div className="row">
                        {/* Current Password */}
                        <div className="col-lg-6 mb-2">
                          <div className="form-group mb-3">
                            <label className="text-label">Current Password*</label>
                            <div className="position-relative password-input-wrapper">
                              <Field
                                type={showCurrentPassword ? 'text' : 'password'}
                                name="currentPassword"
                                className="form-control user-form-input"
                                placeholder="Enter current password"
                              />
                              <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowCurrentPassword(prev => !prev)}
                                aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                              >
                                <i className={`fas ${showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                            <ErrorMessage
                              name="currentPassword"
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        {/* New Password */}
                        <div className="col-lg-6 mb-2">
                          <div className="form-group mb-3">
                            <label className="text-label">New Password*</label>
                            <div className="position-relative password-input-wrapper">
                              <Field
                                type={showNewPassword ? 'text' : 'password'}
                                name="newPassword"
                                className="form-control user-form-input"
                                placeholder="Enter new password"
                              />
                              <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowNewPassword(prev => !prev)}
                                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                              >
                                <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                            <ErrorMessage
                              name="newPassword"
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        {/* Confirm Password */}
                        <div className="col-lg-6 mb-2">
                          <div className="form-group mb-3">
                            <label className="text-label">Confirm Password*</label>
                            <div className="position-relative password-input-wrapper">
                              <Field
                                type={showConfirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                                className="form-control user-form-input"
                                placeholder="Re-enter new password"
                              />
                              <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowConfirmPassword(prev => !prev)}
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                              >
                                <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                            </div>
                            <ErrorMessage
                              name="confirmPassword"
                              component="div"
                              className="text-danger"
                            />
                          </div>
                        </div>

                        {/* Submit */}
                        <div className="text-end toolbar toolbar-bottom p-2">
                          <button
                            type="submit"
                            className="btn user-form-btn sw-btn-next"
                            disabled={isSubmitting}
                            style={{ maxWidth: "100px" }}
                          >
                            {isSubmitting ? (
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : null}
                            Submit
                          </button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </section>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default ChangePassword;
